/** UXP-00005 — AI Content Studio framework (mirrors pkg/contentstudio) */

export const UXP_CS_ID = 'UXP-00005';

export interface ContentScoreSpec {
  dimension: string;
  label: string;
  score: number;
}

export interface SEOScoreSpec {
  analysis: string;
  label: string;
  score: number;
  trend?: 'up' | 'down' | 'stable';
}

export interface DashboardHealthItem {
  label: string;
  score: number;
  status: 'healthy' | 'warning' | 'error' | 'info';
}

export interface ComplianceItem {
  label: string;
  passed: boolean;
  detail: string;
}

export const DASHBOARD_METRICS = [
  'Content Health', 'SEO Health', 'Readability', 'Brand Alignment',
  'Compliance', 'Publishing Status', 'Recommendations', 'Version Summary',
];

export const LISTING_FIELDS = [
  'Titles', 'Bullets', 'Descriptions', 'Backend Search Terms', 'Generic Keywords',
  'Subject Matter', 'Platinum Keywords', 'Marketplace-specific Fields', 'Hidden Search Terms',
];

export const WRITING_ACTIONS = [
  'Generate', 'Rewrite', 'Expand', 'Shorten', 'Summarize',
  'Premium Tone', 'Luxury Tone', 'Technical Tone', 'Lifestyle Tone', 'Localized Tone', 'Seasonal Variants',
];

export const SEO_ANALYSES = [
  'Keyword Coverage', 'Keyword Density', 'Search Intent', 'Competitor Coverage',
  'Semantic Keywords', 'Long-tail Opportunities', 'Ranking Probability', 'Difficulty', 'Opportunity Score',
];

export const KEYWORD_TYPES = [
  'Primary Keywords', 'Secondary Keywords', 'Semantic Keywords', 'Regional Keywords',
  'Seasonal Keywords', 'Negative Keywords', 'AI Suggested Keywords',
];

export const COMPETITOR_DIMENSIONS = [
  'Titles', 'Bullets', 'Descriptions', 'Keywords', 'Content Quality',
  'SEO', 'Brand Messaging', 'Readability', 'Differentiation',
];

export const OPTIMIZATION_TARGETS = [
  'Search Visibility', 'CTR', 'Conversion', 'Readability', 'Brand Voice',
  'Trust Signals', 'Benefit Language', 'Mobile Readability', 'Voice Search Readiness',
];

export const GOVERNANCE_RULES = [
  'Global Rules', 'Organization Rules', 'Brand Rules', 'Marketplace Rules',
  'Category Rules', 'Product DNA', 'Prompt Rules', 'Negative Rules',
];

export const LOCALIZATION_FEATURES = [
  'Marketplace Localization', 'Regional Language Variants', 'Measurements',
  'Currency References', 'Spelling Variants', 'Tone Adaptation', 'Compliance Adaptation',
];

export const COMPLIANCE_CHECKS = [
  'Amazon Policies', 'Restricted Claims', 'Trademark', 'Copyright',
  'Medical Claims', 'Safety Claims', 'Marketplace Requirements',
];

export const SCORING_DIMENSIONS = [
  'SEO Score', 'Readability Score', 'Brand Consistency', 'Compliance Score',
  'Marketplace Readiness', 'Conversion Score', 'Publishing Readiness', 'AI Confidence',
];

export const VERSIONING_FEATURES = [
  'Drafts', 'Versions', 'Approvals', 'Publishing History',
  'Rollback', 'Audit Trail', 'AI Change History', 'Human Change History',
];

export const EXPERIMENT_TYPES = [
  'Title Variants', 'Bullet Variants', 'Description Variants', 'Keyword Experiments',
  'Localized Experiments', 'Seasonal Experiments', 'Expected ROI',
];

export const PUBLISHING_STAGES = [
  'Draft', 'Review', 'Approval', 'Scheduling',
  'Marketplace Selection', 'Release Packages', 'Rollback',
];

export const COPILOT_CAPABILITIES = [
  'Content Explanations', 'Recommendation Reasoning', 'Alternative Versions',
  'Expected Business Impact', 'Simulation', 'Comparison', 'Learning Feedback',
];

export const COLLABORATION_FEATURES = [
  'Assignments', 'Mentions', 'Comments', 'Review Queues', 'Approvals', 'Knowledge Sharing',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Content Dashboard', 'Listing Builder', 'AI Writing Studio', 'SEO Engine',
  'Keyword Engine', 'Competitor Analysis', 'Content Optimization', 'Governance',
  'Compliance & Scoring', 'Versioning & Experimentation', 'Publishing & Copilot',
];

export const ANALYTICS_METRICS = [
  'Content Performance', 'SEO Improvement', 'CTR Improvement', 'Conversion Improvement',
  'AI Adoption', 'Publishing Success', 'Business Impact',
];

export const LIVE_ROUTES: Record<string, string> = {
  listingOptimizer: '/ai/listing',
  creativeStudio: '/ai/creative',
  contentIntel: '/amazon/content-intel',
};

export const DEMO_DASHBOARD_HEALTH: DashboardHealthItem[] = [
  { label: 'Content Health', score: 84, status: 'healthy' },
  { label: 'SEO Health', score: 78, status: 'warning' },
  { label: 'Readability', score: 82, status: 'healthy' },
  { label: 'Brand Alignment', score: 91, status: 'healthy' },
  { label: 'Compliance', score: 96, status: 'healthy' },
  { label: 'Publishing Status', score: 88, status: 'healthy' },
  { label: 'Recommendations', score: 12, status: 'info' },
  { label: 'Version Summary', score: 3, status: 'info' },
];

export const DEMO_CONTENT_SCORES: ContentScoreSpec[] = [
  { dimension: 'seo_score', label: 'SEO Score', score: 82 },
  { dimension: 'readability_score', label: 'Readability', score: 78 },
  { dimension: 'brand_consistency', label: 'Brand Consistency', score: 91 },
  { dimension: 'compliance_score', label: 'Compliance', score: 96 },
  { dimension: 'marketplace_readiness', label: 'Marketplace Readiness', score: 85 },
  { dimension: 'conversion_score', label: 'Conversion Score', score: 74 },
  { dimension: 'publishing_readiness', label: 'Publishing Readiness', score: 88 },
  { dimension: 'ai_confidence', label: 'AI Confidence', score: 87 },
];

export const DEMO_SEO_SCORES: SEOScoreSpec[] = [
  { analysis: 'keyword_coverage', label: 'Keyword Coverage', score: 78, trend: 'up' },
  { analysis: 'keyword_density', label: 'Keyword Density', score: 72, trend: 'stable' },
  { analysis: 'search_intent', label: 'Search Intent', score: 85, trend: 'up' },
  { analysis: 'competitor_coverage', label: 'Competitor Coverage', score: 65, trend: 'down' },
  { analysis: 'semantic_keywords', label: 'Semantic Keywords', score: 70, trend: 'up' },
  { analysis: 'long_tail_opportunities', label: 'Long-tail Opportunities', score: 88, trend: 'up' },
  { analysis: 'ranking_probability', label: 'Ranking Probability', score: 76, trend: 'stable' },
  { analysis: 'difficulty', label: 'Difficulty', score: 62, trend: 'stable' },
  { analysis: 'opportunity_score', label: 'Opportunity Score', score: 84, trend: 'up' },
];

export const DEMO_KEYWORDS: Record<string, string[]> = {
  primary: ['wireless earbuds', 'bluetooth earphones', 'noise cancelling'],
  secondary: ['premium audio', 'long battery', 'comfort fit'],
  semantic: ['true wireless', 'in-ear headphones', 'active noise cancellation'],
  regional: ['earbuds india', 'bluetooth headset hindi market'],
  seasonal: ['festive gift earbuds', 'monsoon waterproof'],
  negative: ['cheap', 'knockoff', 'refurbished'],
  suggested: ['anc earbuds pro', 'ipx5 waterproof', '40hr battery life'],
};

export const DEMO_COPILOT_INSIGHTS = [
  'Title rewrite adds primary keyword in first 80 chars — estimated +12% organic visibility',
  'Bullet 3 uses feature language; recommend benefit-first rewrite for +8% conversion',
  'Backend terms missing 2 high-volume long-tail keywords identified by competitor analysis',
  'Brand voice deviation detected in description paragraph 2 — luxury tone mismatch',
  'Compliance: remove \'best in class\' superlative — Amazon policy restriction',
];

export const DEMO_COMPLIANCE: ComplianceItem[] = [
  { label: 'Amazon Policies', passed: true, detail: 'No policy violations detected' },
  { label: 'Restricted Claims', passed: false, detail: 'Superlative \'best in class\' found in bullet 2' },
  { label: 'Trademark', passed: true, detail: 'No unauthorized trademark usage' },
  { label: 'Copyright', passed: true, detail: 'Original content verified' },
  { label: 'Medical Claims', passed: true, detail: 'N/A for product category' },
  { label: 'Safety Claims', passed: true, detail: 'IPX5 rating substantiated' },
  { label: 'Marketplace Requirements', passed: true, detail: 'Amazon.in field requirements met' },
];

export const DEMO_LISTING = {
  title: 'SoundWave Premium Wireless Earbuds Pro — Active Noise Cancellation, 40Hr Battery',
  bullets: [
    'ACTIVE NOISE CANCELLATION — Block distractions with hybrid ANC technology for immersive audio',
    '40-HOUR BATTERY LIFE — Extended listening with charging case; 10-min quick charge for 2 hours',
    'IPX5 WATER RESISTANT — Sweat and splash proof for workouts and daily commutes',
    'PREMIUM COMFORT FIT — Ergonomic design with 3 ear tip sizes for all-day wear',
    'CRYSTAL CLEAR CALLS — Dual beamforming mics with AI noise reduction for HD voice',
  ],
  description: 'Experience premium wireless audio with SoundWave Pro earbuds...',
  backendKeywords: 'wireless earbuds bluetooth noise cancelling anc ipx5 waterproof',
};

export function buildContentStudioDashboard() {
  return {
    dashboard_metrics: DASHBOARD_METRICS.length,
    listing_fields: LISTING_FIELDS.length,
    writing_actions: WRITING_ACTIONS.length,
    seo_analyses: SEO_ANALYSES.length,
    keyword_types: KEYWORD_TYPES.length,
    scoring_dimensions: SCORING_DIMENSIONS.length,
    governance_rules: GOVERNANCE_RULES.length,
    compliance_checks: COMPLIANCE_CHECKS.length,
    copilot_capabilities: COPILOT_CAPABILITIES.length,
  };
}
